import React, { useEffect } from 'react';
import { Mail } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation.js';
import { brandText } from '../components/BrandText.jsx';
import Footer from '../components/Footer.jsx';
import { applyPageSeo } from '../utils/seo.js';
import { withLinks } from '../utils/textLinks.jsx';

const DetailCard = ({ item, tone = 'primary' }) => (
  <article className="homepage-card p-5 md:p-6">
    <div className="mb-4">
      <h3 className="text-xl font-black tracking-[-0.025em] text-text-primary mb-2">{item.title}</h3>
      {item.subtitle && <p className={`text-sm font-bold text-${tone} mb-2`}>{item.subtitle}</p>}
    </div>
    <p className="text-text-secondary text-sm leading-relaxed">{item.description}</p>
  </article>
);

const SectionHeader = ({ title, subtitle }) => (
  <header className="site-section-head">
    <h2>{title}</h2>
    <p>{subtitle}</p>
  </header>
);

const KyveilPage = () => {
  const { t, currentLang } = useTranslation('kyveil');

  useEffect(() => {
    applyPageSeo({ ...t.seo, pageId: 'kyveil' }, currentLang);
  }, [t.seo, currentLang]);

  const mailTo = (subject) => `mailto:${t.cta?.email}?subject=${encodeURIComponent(subject || '')}`;

  return (
    <div className="min-h-screen bg-bg-main text-text-primary">
      <section className="page-head pb-8 md:pb-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-[1fr_0.78fr] gap-8 lg:gap-12 items-end">
            <div>
              <div className="editorial-kicker mb-4">{t.hero?.eyebrow}</div>
              <h1 className="site-title">
                {t.hero?.title}
              </h1>
              <p className="site-lead">
                {t.hero?.subtitle}
              </p>
              <p className="site-copy">
                {withLinks(t.hero?.description || '', currentLang)}
              </p>
            </div>
            <div className="homepage-card p-6 md:p-8 bg-bg-hover">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-primary-light mb-5">{t.hero?.platforms}</p>
              <div className="space-y-4 text-text-secondary leading-relaxed">
                {t.hero?.proofPoints?.map((point, index) => (
                  <div key={index}>{point}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="homepage-section homepage-muted-panel">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeader title={t.dataSovereignty?.title} subtitle={t.dataSovereignty?.subtitle} />
          <div className="grid md:grid-cols-2 gap-5">
            {t.dataSovereignty?.items?.map((item, index) => (
              <DetailCard key={index} item={item} />
            ))}
          </div>
        </div>
      </section>

      <section className="homepage-section homepage-muted-panel">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeader title={t.anonChat?.title} subtitle={t.anonChat?.subtitle} />
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-6 items-stretch">
            <article className="homepage-card p-6 md:p-8 bg-bg-hover">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-primary-light mb-4">{t.anonChat?.eyebrow}</p>
              <h3 className="text-3xl md:text-4xl font-black tracking-[-0.045em] text-text-primary mb-4">
                {t.anonChat?.name}
              </h3>
              <p className="text-text-secondary leading-relaxed mb-5">{t.anonChat?.description}</p>
              <p className="text-lg font-black text-text-primary leading-relaxed">{t.anonChat?.statement}</p>
            </article>
            <div className="grid md:grid-cols-2 gap-4">
              {t.anonChat?.steps?.map((item, index) => (
                <DetailCard key={index} item={item} tone={item.tone || 'primary'} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="homepage-section">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeader title={t.deployment?.title} subtitle={t.deployment?.subtitle} />
          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            {t.deployment?.options?.map((option, index) => (
              <article key={index} className="homepage-card p-6 md:p-8">
                <p className="text-sm font-black uppercase tracking-[0.16em] text-primary-light mb-3">{option.subtitle}</p>
                <h3 className="text-3xl font-black tracking-[-0.04em] text-text-primary mb-4">{option.title}</h3>
                <p className="text-text-secondary leading-relaxed mb-6">{option.description}</p>
                <div className="space-y-3">
                  {option.features?.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-start gap-3 text-sm text-text-secondary">
                      <span className="text-primary font-black">+</span>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
          <p className="site-statement site-statement--large">
            {t.deployment?.bothNote}
          </p>
        </div>
      </section>

      <section className="homepage-section homepage-muted-panel">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeader title={t.features?.title} subtitle={t.features?.subtitle} />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.features?.items?.map((item, index) => (
              <DetailCard key={index} item={item} tone={item.tone || 'primary'} />
            ))}
          </div>
        </div>
      </section>

      <section className="homepage-section">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeader title={t.technology?.title} subtitle={t.technology?.subtitle} />
          <div className="grid md:grid-cols-3 gap-5 mb-6">
            {t.technology?.items?.map((item, index) => (
              <DetailCard key={index} item={item} tone={item.tone || 'primary'} />
            ))}
          </div>
          <div className="homepage-card p-6 bg-bg-hover text-center">
            <p className="text-lg font-black text-text-primary mb-2">{t.technology?.integration}</p>
            <p className="text-text-secondary leading-relaxed">{t.technology?.integrationDesc}</p>
          </div>
        </div>
      </section>

      <section className="homepage-section homepage-muted-panel">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeader title={t.technicalDetails?.title} subtitle={t.technicalDetails?.subtitle} />
          <div className="grid md:grid-cols-2 gap-5">
            {t.technicalDetails?.items?.map((item, index) => (
              <DetailCard key={index} item={item} tone={item.tone || 'primary'} />
            ))}
          </div>
        </div>
      </section>

      <section className="homepage-section">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeader title={t.useCases?.title} subtitle={t.useCases?.subtitle} />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {t.useCases?.items?.map((item, index) => (
              <DetailCard key={index} item={item} tone={item.tone || 'primary'} />
            ))}
          </div>
        </div>
      </section>

      <section className="homepage-section homepage-muted-panel">
        <div className="max-w-5xl mx-auto px-4">
          <SectionHeader title={t.releases?.title} subtitle={t.releases?.subtitle} />
          <div className="space-y-4">
            {t.releases?.items?.map((item, index) => (
              <DetailCard key={index} item={item} tone={item.tone || 'primary'} />
            ))}
          </div>
        </div>
      </section>

      <section className="pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="site-contact">
            <div>
              <h2 className="site-heading">{t.cta?.title}</h2>
              {t.cta?.subtitle && <p className="finance-cta__lead">{t.cta?.subtitle}</p>}
            </div>
            <div className="flex flex-wrap gap-3">
              <a href={mailTo(t.cta?.desktopSubject)} className="homepage-button-primary">
                {t.cta?.desktop}
              </a>
              <a href={mailTo(t.cta?.apiSubject)} className="homepage-button-secondary">
                {t.cta?.apiContact}
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />

    </div>
  );
};

export default KyveilPage;
